/**
 * The one core importer of the custom manifest's SLOTS (E5 spec §7.2, D56;
 * register columns: SA-453; home widgets: Home spec 2026-09-23 D2, §3.3; app
 * listeners: SA-1143): record cards, task views, register views, register
 * columns, home widgets, home tiles, app listeners.
 * The mount points (`RecordDetailCards`, `TaskDetailPage`, `OrdersPage`, the
 * Home page, `AppShell`) import this file and never `@/custom`.
 *
 * Two rules every export keeps:
 * - Access is STRICT — nothing contributes until the feature manifest AND the
 *   permissions have loaded. `useCanAccessFeature` assumes "allowed" before
 *   load (right for nav chrome); here that would swap a module's view in for
 *   the generated form, and fetch its chunk, on a deployment that never
 *   enabled it. After load the answer is `useCanAccessFeature`'s.
 * - Every lazy component sits in its own Suspense inside a `SlotBoundary`, so
 *   a module fault never blanks a core page. A task view falls back to the
 *   generated form and a register view to the built-in register: the work is
 *   still there.
 *
 * No React Query here: `OrdersPage` calls `useRegisterViews` and must stay
 * free of it (SA-397). A module's own component may use it.
 */
import { Suspense, useCallback, useMemo, type ComponentType, type ReactNode } from 'react'
import { Spinner } from '@/components/ui'
import { TaskForm } from '@/components/process/TaskForm'
import { RegisterView, type RegisterViewProps } from '@/components/records/RegisterView'
import type { SortDir } from '@/components/records/registerSort'
import {
  APP_LISTENERS, HOME_TILES, HOME_WIDGETS, RECORD_CARDS, REGISTER_COLUMNS, REGISTER_VIEWS, TASK_VIEWS,
  type AppListenerEntry, type RecordCardEntry, type RecordCardProps, type RegisterColumnsEntry, type RegisterViewEntry, type TaskViewEntry, type TaskViewProps,
} from '@/custom'
import { useFeatureStore } from '@/stores/featureStore'
import { usePermissionStore } from '@/stores/permissionStore'
import type { TaskRead } from '@/types/process'
import { TileDash } from '@/pages/Home/tiles/tileParts'
import type { HomeTileEntry, HomeTileProps, HomeWidgetEntry, HomeWidgetProps } from './homeWidgetTypes'
import { SlotBoundary } from './SlotBoundary'
import { TellUsLink } from '@/components/helpFeedback/TellUsLink'

type AccessCheck = (feature: string, permission: string) => boolean

/** One fixed set of store reads, whatever the manifest holds — so a list of
 * entries is filtered without calling a hook per entry. */
function useSlotAccess(): AccessCheck {
  const featuresLoaded = useFeatureStore((s) => s.isLoaded)
  const features = useFeatureStore((s) => s.features)
  const permissionsLoaded = usePermissionStore((s) => s.isLoaded)
  const permissions = usePermissionStore((s) => s.permissions)
  return useCallback(
    (feature, permission) =>
      featuresLoaded && permissionsLoaded && features.has(feature) && permissions.has(permission),
    [featuresLoaded, permissionsLoaded, features, permissions],
  )
}

const Loading = () => <div className="flex justify-center py-6"><Spinner /></div>

// ── Record cards ─────────────────────────────────────────────────────

export type RecordCardColumn = 'work' | 'enquiry'

/** The enabled manifest cards of one column, in `order`: `work` after the
 * built-in work cards, `enquiry` at the top of the enquiry column (SA-451).
 * Mounted inside the record lane only, so a card may use React Query. */
export function RecordCardSlot({
  order, onChanged, column = 'work', entries = RECORD_CARDS,
}: RecordCardProps & { column?: RecordCardColumn; entries?: RecordCardEntry[] }) {
  const allowed = useSlotAccess()
  const cards = entries
    .filter((entry) => (entry.column ?? 'work') === column && allowed(entry.feature, entry.permission))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  return (
    <>
      {cards.map((entry) => (
        <SlotBoundary
          key={entry.id}
          name={entry.id}
          resetKey={order.id}
          fallback={
            <p role="status" className="text-xs text-muted-foreground">
              A card on this page could not be shown.{' '}
              <TellUsLink message="A card on this page could not be shown." />
            </p>
          }
        >
          <Suspense fallback={null}>
            <entry.Component order={order} onChanged={onChanged} />
          </Suspense>
        </SlotBoundary>
      ))}
    </>
  )
}

// ── Task views ───────────────────────────────────────────────────────

/** Step id first, then the entry's own contract check (D65). The parity test
 * refuses two entries naming one step, so there is at most one candidate. A
 * `matches` that throws is a module fault: the generated form renders. */
function findTaskView(task: TaskRead, entries: TaskViewEntry[]): TaskViewEntry | null {
  const entry = entries.find((candidate) => candidate.steps.includes(task.step_id))
  if (!entry) return null
  if (!entry.matches) return entry
  try {
    return entry.matches(task) ? entry : null
  } catch (error) {
    console.error(`Custom task view '${entry.id}' failed its contract check:`, error)
    return null
  }
}

/** One guarded component per entry, made once: a new component identity on
 * every render would remount the view and drop what the person typed. */
const guardedTaskViews = new WeakMap<TaskViewEntry, ComponentType<TaskViewProps>>()

function guardedTaskView(entry: TaskViewEntry): ComponentType<TaskViewProps> {
  let Guarded = guardedTaskViews.get(entry)
  if (!Guarded) {
    const View = entry.Component
    Guarded = function GuardedTaskView(props: TaskViewProps) {
      return (
        <SlotBoundary name={entry.id} resetKey={props.task.id} fallback={<TaskForm {...props} />}>
          <Suspense fallback={<Loading />}>
            <View {...props} />
          </Suspense>
        </SlotBoundary>
      )
    }
    guardedTaskViews.set(entry, Guarded)
  }
  return Guarded
}

/** The custom view for this task, or null for the generated form: no entry
 * names the step, its `matches` says no, or the viewer lacks the entry's flag
 * or permission. The candidate is chosen without a hook, so exactly one
 * access read runs per render whatever the manifest holds. */
export function useTaskView(task: TaskRead, entries: TaskViewEntry[] = TASK_VIEWS): ComponentType<TaskViewProps> | null {
  const allowed = useSlotAccess()
  const entry = findTaskView(task, entries)
  return entry && allowed(entry.feature, entry.permission) ? guardedTaskView(entry) : null
}

// ── Register views ───────────────────────────────────────────────────

/** Enabled entries only, in declaration order: what the view switch lists. */
export function useRegisterViews(entries: RegisterViewEntry[] = REGISTER_VIEWS): RegisterViewEntry[] {
  const allowed = useSlotAccess()
  return useMemo(() => entries.filter((entry) => allowed(entry.feature, entry.permission)), [entries, allowed])
}

/** SA-1183: every register view's module sorts, key → first click, whatever its flag: the
 *  page reads them from the URL at mount, before either store has loaded. The server sorts by
 *  nothing a viewer may not use (spec R5), so knowing a key offers nothing. */
export function moduleSorts(entries: RegisterViewEntry[] = REGISTER_VIEWS): Record<string, SortDir> {
  return Object.assign({}, ...entries.map((entry) => entry.sorts ?? {}))
}

/** One register view, over the same props the built-in register takes. */
export function RegisterViewSlot({ entry, ...props }: RegisterViewProps & { entry: RegisterViewEntry }) {
  const View = entry.Component
  return (
    <SlotBoundary name={entry.id} resetKey={entry.id} fallback={<RegisterView {...props} />}>
      <Suspense fallback={<Loading />}>
        <View {...props} />
      </Suspense>
    </SlotBoundary>
  )
}

// ── Register columns ─────────────────────────────────────────────────

/** One guarded component per entry, made once: a fresh identity each render
 * would remount the table and drop the page's scroll position and the
 * module's cached read. */
const guardedColumnProviders = new WeakMap<RegisterColumnsEntry, ComponentType<RegisterViewProps>>()

function guardedColumnProvider(entry: RegisterColumnsEntry): ComponentType<RegisterViewProps> {
  let Guarded = guardedColumnProviders.get(entry)
  if (!Guarded) {
    const Provider = entry.Component
    Guarded = function GuardedRegisterColumns(props: RegisterViewProps) {
      return (
        <SlotBoundary name={entry.id} resetKey={entry.id} fallback={<RegisterView {...props} />}>
          <Suspense fallback={<RegisterView {...props} />}>
            <Provider {...props} />
          </Suspense>
        </SlotBoundary>
      )
    }
    guardedColumnProviders.set(entry, Guarded)
  }
  return Guarded
}

/** The built-in register, decorated by the first enabled module entry: the
 * module renders the CORE table with its own columns, so the core never
 * learns what those columns mean and a fault costs the columns, not the page
 * (SA-453 D1). At most one entry contributes — composing columns across
 * modules would need a hook per entry, which `useSlotAccess` forbids (D3). */
export function RegisterColumnsSlot({
  entries = REGISTER_COLUMNS, ...props
}: RegisterViewProps & { entries?: RegisterColumnsEntry[] }) {
  const allowed = useSlotAccess()
  const entry = entries.find((candidate) => allowed(candidate.feature, candidate.permission)) ?? null
  if (!entry) return <RegisterView {...props} />
  const Guarded = guardedColumnProvider(entry)
  return <Guarded {...props} />
}

// ── Home widgets ─────────────────────────────────────────────────────

/** Every registered widget, core first, and whether the viewer may see each
 * (strict: both stores loaded and both saying yes). The page resolves the
 * stored layout over `all` and draws only what `canSee` allows, so an
 * inaccessible widget stays in the preference and comes back with the
 * permission (Home spec D3). One fixed set of store reads, whatever the
 * lists hold. */
export function useHomeWidgets(
  core: HomeWidgetEntry[], entries: HomeWidgetEntry[] = HOME_WIDGETS,
): { all: HomeWidgetEntry[]; canSee: (id: string) => boolean } {
  const allowed = useSlotAccess()
  return useMemo(() => {
    const all = [...core, ...entries]
    const visible = new Set(all.filter((e) => allowed(e.feature, e.permission)).map((e) => e.id))
    return { all, canSee: (id: string) => visible.has(id) }
  }, [core, entries, allowed])
}

/** One widget inside its own fence: a fault or a failed chunk becomes
 * `fallback` (one quiet line inside the card), never a missing card. `view`
 * picks the widget or, on a card narrower than its minimum, its headline
 * (SA-1406); each has its own fence, so a fault in one never sticks to the other. */
export function HomeWidgetSlot({ entry, fallback, view = 'full', ...props }: HomeWidgetProps & {
  entry: HomeWidgetEntry; fallback: ReactNode; view?: 'full' | 'headline'
}) {
  const Widget = view === 'headline' ? entry.Headline : entry.Component
  return (
    <SlotBoundary name={entry.id} resetKey={`${entry.id}:${view}`} fallback={fallback}>
      <Suspense fallback={<Loading />}>
        <Widget {...props} />
      </Suspense>
    </SlotBoundary>
  )
}

// ── Home tiles ───────────────────────────────────────────────────────

/** The tiles the viewer may see (SA-1171, spec §2.4), core and module
 * together, by `order` (declaration order breaks ties: `sort` is stable).
 * Unlike `useHomeWidgets` this FILTERS: an inaccessible tile is simply
 * absent, and the widget's hidden set is keyed by id, so its choice waits
 * for the permission. One fixed set of store reads, whatever the lists hold. */
export function useHomeTiles(core: HomeTileEntry[], entries: HomeTileEntry[] = HOME_TILES): HomeTileEntry[] {
  const allowed = useSlotAccess()
  return useMemo(
    () => [...core, ...entries].filter((e) => allowed(e.feature, e.permission)).sort((a, b) => a.order - b.order),
    [core, entries, allowed],
  )
}

/** One tile inside its own fence: a fault or a failed chunk is the tile's
 * label over a dash, never a missing cell. */
export function HomeTileSlot({ entry, width }: HomeTileProps & { entry: HomeTileEntry }) {
  const Tile = entry.Component
  const dash = <TileDash id={entry.id} title={entry.title} />
  return (
    <SlotBoundary name={entry.id} resetKey={entry.id} fallback={dash}>
      <Suspense fallback={dash}>
        <Tile width={width} />
      </Suspense>
    </SlotBoundary>
  )
}

// ── App listeners ────────────────────────────────────────────────────

/** Every enabled app listener, mounted once in the app shell (SA-1143). A fault
 * renders nothing: a listener has no visible part to fall back to, and the page
 * around it must never notice. */
export function AppListenersSlot({ entries = APP_LISTENERS }: { entries?: AppListenerEntry[] }) {
  const allowed = useSlotAccess()
  return (
    <>
      {entries.filter((entry) => allowed(entry.feature, entry.permission)).map((entry) => (
        <SlotBoundary key={entry.id} name={entry.id} fallback={null}>
          <Suspense fallback={null}>
            <entry.Component />
          </Suspense>
        </SlotBoundary>
      ))}
    </>
  )
}
